import { KeyRound, ShieldOff, UserCheck, UserMinus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ShareDialog } from '@/components/share-dialog';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatDate, formatRelative } from '@/lib/utils';

import {
  resetPasswordResultSchema,
  useAddUser,
  useUserAction,
  type UserAction,
} from './admin-actions-api';
import { ROLES, type AdminUser, type Invite, type Role } from './admin-schemas';
import { ConfirmActionDialog } from './confirm-action-dialog';

/**
 * משתמשים — בחיפוש הכללי ובמגירת העסק. רק השדות שהמפעיל רשאי לראות. חשבון
 * מפעיל מוצג בלי פעולות (השרת חוסם ממילא).
 */
export function UserList({ users, showBusiness }: { users: AdminUser[]; showBusiness?: boolean }) {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const action = useUserAction();
  const [confirm, setConfirm] = useState<{ user: AdminUser; action: UserAction } | null>(null);
  const [invite, setInvite] = useState<{ name: string; invite: Invite } | null>(null);

  const run = (user: AdminUser, kind: UserAction) =>
    action.mutate(
      { businessId: user.tenantId, userId: user.id, action: kind },
      {
        onSuccess: (res) => {
          setConfirm(null);
          if (kind === 'reset-password') {
            const parsed = resetPasswordResultSchema.safeParse(res);
            if (parsed.success) setInvite({ name: user.name, invite: parsed.data });
          } else {
            toast.success(t(`users.done.${kind}`, { name: user.name }));
          }
        },
        onError: (err) =>
          toast.error(t('users.failed'), err instanceof ApiError ? err.message : undefined),
      },
    );

  if (users.length === 0)
    return <p className="py-6 text-center text-sm text-fg-muted">{t('users.empty')}</p>;

  return (
    <>
      <ul className="divide-y divide-border">
        {users.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
            <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
              <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-fg">
                {u.name}
                <Badge tone="violet" dot={false}>
                  {t(`roles.${u.role}`)}
                </Badge>
                {!u.isActive ? <Badge tone="neutral">{t('users.inactive')}</Badge> : null}
                {u.isAccountCreator ? (
                  <Badge tone="accent" dot={false}>
                    {t('users.creator')}
                  </Badge>
                ) : null}
                {u.isPlatformOperator ? (
                  <Badge tone="info" dot={false}>
                    {t('users.operator')}
                  </Badge>
                ) : null}
                <Badge tone={u.mfaEnabled ? 'success' : 'neutral'}>
                  {u.mfaEnabled ? t('users.mfaOn') : t('users.mfaOff')}
                </Badge>
              </p>
              <p className="truncate text-xs text-fg-muted">
                <span dir="ltr" className="ltr-inline">
                  {u.email}
                </span>
                {showBusiness ? ` · ${u.businessName}` : ''}
                {` · ${
                  u.lastLoginAt
                    ? t('users.lastLogin', { when: formatRelative(u.lastLoginAt) })
                    : t('users.neverLoggedIn')
                }`}
                {` · ${t('users.created', { date: formatDate(u.createdAt) })}`}
              </p>
            </div>
            {u.isPlatformOperator ? null : (
              <div className="flex flex-wrap gap-1">
                {u.isActive ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirm({ user: u, action: 'reset-password' })}
                    >
                      <KeyRound aria-hidden />
                      {t('users.actions.reset-password')}
                    </Button>
                    {u.mfaEnabled ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirm({ user: u, action: 'reset-mfa' })}
                      >
                        <ShieldOff aria-hidden />
                        {t('users.actions.reset-mfa')}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirm({ user: u, action: 'deactivate' })}
                    >
                      <UserMinus aria-hidden />
                      {t('users.actions.deactivate')}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirm({ user: u, action: 'reactivate' })}
                  >
                    <UserCheck aria-hidden />
                    {t('users.actions.reactivate')}
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {confirm ? (
        <ConfirmActionDialog
          title={t(`users.confirm.${confirm.action}.title`, { name: confirm.user.name })}
          description={t(`users.confirm.${confirm.action}.description`)}
          confirmLabel={t(`users.actions.${confirm.action}`)}
          danger={confirm.action !== 'reactivate'}
          loading={action.isPending}
          onClose={() => setConfirm(null)}
          onConfirm={() => run(confirm.user, confirm.action)}
        />
      ) : null}

      {invite ? (
        <ShareDialog
          result={{
            url: invite.invite.loginUrl,
            waUrl: invite.invite.waUrl,
            message: invite.invite.message,
          }}
          title={t('users.passwordTitle', { name: invite.name })}
          description={t('users.passwordDescription')}
          onClose={() => setInvite(null)}
        />
      ) : null}
    </>
  );
}

/** הוספת משתמש לעסק: תפקיד + סיסמה זמנית, כמו "הוסף לצוות". */
export function AddUserDialog({
  businessId,
  onClose,
}: {
  businessId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const toast = useToast();
  const add = useAddUser();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<Role>('MANAGER');
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<{ name: string; invite: Invite } | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError(t('create.errors.ownerName'));
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return setError(t('create.errors.ownerEmail'));
    setError(null);
    add.mutate(
      {
        businessId,
        input: {
          name: name.trim(),
          role,
          ...(email.trim() ? { email: email.trim().toLowerCase() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        },
      },
      {
        onSuccess: (r) => setInvite({ name: r.member.name, invite: r }),
        onError: (err) =>
          toast.error(t('users.failed'), err instanceof ApiError ? err.message : undefined),
      },
    );
  };

  if (invite)
    return (
      <ShareDialog
        result={{
          url: invite.invite.loginUrl,
          waUrl: invite.invite.waUrl,
          message: invite.invite.message,
        }}
        title={t('users.passwordTitle', { name: invite.name })}
        description={t('users.passwordDescription')}
        onClose={onClose}
      />
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="add-user-desc">
        <DialogHeader>
          <DialogTitle>{t('users.addTitle')}</DialogTitle>
          <DialogDescription id="add-user-desc">{t('users.addDescription')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate>
          <DialogBody>
            <Field label={t('users.name')} htmlFor="au-name" error={error ?? undefined} required>
              <Input
                id="au-name"
                autoFocus
                autoComplete="off"
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label={t('users.role')} htmlFor="au-role">
              <Select value={role} onValueChange={(v) => setRole(v as Role)}>
                <SelectTrigger id="au-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`roles.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('users.email')} htmlFor="au-email">
              <Input
                id="au-email"
                type="email"
                dir="ltr"
                autoComplete="off"
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field
              label={t('create.ownerPhone')}
              htmlFor="au-phone"
              hint={t('create.ownerPhoneHint')}
            >
              <Input
                id="au-phone"
                type="tel"
                dir="ltr"
                autoComplete="off"
                maxLength={40}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t('common:actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" loading={add.isPending}>
              {t('users.addSubmit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
